import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent, type RefObject, type TouchEvent as ReactTouchEvent } from "react";

/**
 * Hold and drag on the levels board (#ai-feedback 2026-10-26, card #4, version B of the phone test).
 * The browser's own drag works badly with touch on iPhone Safari and needs the exact drop place.
 *
 * - Touch: hold a card for `HOLD_MS` until it lifts, then drag. A finger that moves before the hold
 *   ends is a scroll, not a drag.
 * - Mouse: the card lifts after a move of a few pixels.
 * - The whole box of a column is the drop area. Between two boxes, the nearest box within
 *   `NEAR_PX` wins. The page scrolls when the finger is near the top or bottom edge.
 * - A drop outside every box, Escape, a cancelled touch, a second finger, the window losing focus,
 *   or a mouse button released outside the window: the card flies back.
 *
 * The board marks each column box with `data-board-column` and each card with `data-participant`.
 * The lifted copy follows the finger in `document.body`; the card stays in place, dimmed.
 */

const HOLD_MS = 230;
/** A finger that moves more than this before the hold ends is scrolling. */
const SCROLL_SLOP_PX = 10;
/** A mouse that moves more than this with the button down starts the drag. */
const MOUSE_SLOP_PX = 4;
const NEAR_PX = 60;
const EDGE_PX = 90;
const MAX_SCROLL_PX = 16;

type Drag = {
  id: string;
  card: HTMLElement;
  touch: boolean;
  /** The finger that started a touch drag: only it moves and drops the card. */
  touchId: number | null;
  x: number;
  y: number;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
  lifted: boolean;
  ghost: HTMLElement | null;
  target: string | null;
  timer: ReturnType<typeof setTimeout> | null;
  frame: number;
};

/** The column under a point: the box that holds it, else the nearest box within `NEAR_PX`. */
export function columnAt(board: HTMLElement, x: number, y: number): string | null {
  let best: string | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const box of board.querySelectorAll<HTMLElement>("[data-board-column]")) {
    const rect = box.getBoundingClientRect();
    const dx = Math.max(rect.left - x, 0, x - rect.right);
    const dy = Math.max(rect.top - y, 0, y - rect.bottom);
    const distance = Math.hypot(dx, dy);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = box.dataset.boardColumn ?? null;
    }
  }
  return bestDistance <= NEAR_PX ? best : null;
}

function reducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export function useHoldDrag({
  board,
  onDrop,
}: {
  board: RefObject<HTMLElement | null>;
  /** A card dropped on a column. `from` is where the lifted copy was, for the slide to its new place. */
  onDrop: (participantId: string, column: string, from: DOMRect) => void;
}) {
  const drag = useRef<Drag | null>(null);
  const dropRef = useRef(onDrop);
  // The latest drop handler, for the document listeners set up once.
  useEffect(() => {
    dropRef.current = onDrop;
  });
  /** A mouse drag ends with a click on what is under the button; that click must not choose a card. */
  const swallowClick = useRef(false);
  const [pressing, setPressing] = useState<string | null>(null);
  const [lifted, setLifted] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  /** `lift` lives in the effect with the document listeners; the hold timer calls it through this. */
  const liftRef = useRef<() => void>(() => {});
  /** Ends a drag that is still open when a new press starts (its end event never came). */
  const finishRef = useRef<(drop: boolean) => void>(() => {});

  useEffect(() => {
    function follow(state: Drag) {
      if (!state.lifted || !state.ghost) return;
      state.ghost.style.transform = `translate3d(${state.x - state.offsetX}px, ${state.y - state.offsetY}px, 0) scale(1.03) rotate(-1deg)`;
      const target = board.current ? columnAt(board.current, state.x, state.y) : null;
      if (target !== state.target) {
        state.target = target;
        setOver(target);
      }
    }

    function autoScroll() {
      const state = drag.current;
      if (!state || !state.lifted) return;
      const height = window.innerHeight;
      let speed = 0;
      if (state.y < EDGE_PX) speed = -MAX_SCROLL_PX * (1 - state.y / EDGE_PX);
      else if (state.y > height - EDGE_PX) speed = MAX_SCROLL_PX * (1 - (height - state.y) / EDGE_PX);
      if (speed) {
        window.scrollBy(0, speed);
        follow(state);
      }
      state.frame = requestAnimationFrame(autoScroll);
    }

    function lift() {
      const state = drag.current;
      if (!state || state.lifted) return;
      state.lifted = true;
      const rect = state.card.getBoundingClientRect();
      const ghost = state.card.cloneNode(true) as HTMLElement;
      for (const element of ghost.querySelectorAll("[id]")) element.removeAttribute("id");
      ghost.removeAttribute("data-participant");
      ghost.setAttribute("aria-hidden", "true");
      // A picture only: no Tab stop and no control in the copy (internal review F3.1).
      ghost.inert = true;
      for (const element of ghost.querySelectorAll("button, select, input, textarea, a[href], [tabindex]")) element.setAttribute("tabindex", "-1");
      Object.assign(ghost.style, {
        position: "fixed",
        left: "0px",
        top: "0px",
        margin: "0px",
        width: `${rect.width}px`,
        zIndex: "60",
        pointerEvents: "none",
        cursor: "grabbing",
        willChange: "transform",
        boxShadow: "0 16px 36px rgba(17,26,43,.26)",
        opacity: "1",
      });
      document.body.appendChild(ghost);
      state.ghost = ghost;
      setPressing(null);
      setLifted(state.id);
      try {
        navigator.vibrate?.(12);
      } catch {
        // Not every phone lets a page vibrate.
      }
      follow(state);
      state.frame = requestAnimationFrame(autoScroll);
    }

    function finish(drop: boolean) {
      const state = drag.current;
      if (!state) return;
      drag.current = null;
      if (state.timer) clearTimeout(state.timer);
      cancelAnimationFrame(state.frame);
      setPressing(null);
      setOver(null);
      setLifted(null);
      if (!state.lifted || !state.ghost) return;
      if (!state.touch) swallowClick.current = true;
      const ghost = state.ghost;
      const from = ghost.getBoundingClientRect();
      if (drop && state.target) {
        ghost.remove();
        dropRef.current(state.id, state.target, from);
        return;
      }
      // No drop: the copy flies back to the card.
      const home = state.card.getBoundingClientRect();
      if (typeof ghost.animate === "function" && !reducedMotion()) {
        const flight = ghost.animate([{ transform: ghost.style.transform }, { transform: `translate3d(${home.left}px, ${home.top}px, 0)` }], {
          duration: 220,
          easing: "ease-out",
        });
        flight.onfinish = () => ghost.remove();
        flight.oncancel = () => ghost.remove();
      } else {
        ghost.remove();
      }
    }

    function ownTouch(list: TouchList, state: Drag): Touch | null {
      for (const touch of Array.from(list)) if (touch.identifier === state.touchId) return touch;
      return null;
    }
    function onTouchStartAnywhere(event: TouchEvent) {
      // A second finger is a pinch or a mistake, not a drop: the card goes back.
      if (drag.current?.touch && event.touches.length > 1) finish(false);
    }
    function onTouchMove(event: TouchEvent) {
      const state = drag.current;
      if (!state || !state.touch) return;
      const touch = ownTouch(event.touches, state);
      if (!touch) return;
      state.x = touch.clientX;
      state.y = touch.clientY;
      if (!state.lifted) {
        if (Math.hypot(state.x - state.startX, state.y - state.startY) > SCROLL_SLOP_PX) finish(false);
        return;
      }
      // Lifted: the finger moves the card, not the page.
      event.preventDefault();
      follow(state);
    }
    function onTouchEnd(event: TouchEvent) {
      const state = drag.current;
      // Only the end of the finger that started the drag drops the card.
      if (!state || !state.touch || !ownTouch(event.changedTouches, state)) return;
      // No click on what is under the finger after a drop.
      if (state.lifted) event.preventDefault();
      finish(true);
    }
    function onTouchCancel() {
      if (drag.current?.touch) finish(false);
    }
    function onMouseMove(event: MouseEvent) {
      const state = drag.current;
      if (!state || state.touch) return;
      // The button came up outside the window: no mouseup reached the page. The card goes back.
      if (event.buttons === 0) {
        finish(false);
        return;
      }
      state.x = event.clientX;
      state.y = event.clientY;
      if (!state.lifted && Math.hypot(state.x - state.startX, state.y - state.startY) > MOUSE_SLOP_PX) lift();
      follow(state);
    }
    function onMouseUp() {
      if (drag.current && !drag.current.touch) finish(true);
    }
    function onLostFocus() {
      if (drag.current) finish(false);
    }
    function onVisibility() {
      if (document.visibilityState === "hidden") onLostFocus();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && drag.current) finish(false);
    }
    function onClick(event: MouseEvent) {
      if (!swallowClick.current) return;
      swallowClick.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
    function onMouseDownAnywhere() {
      swallowClick.current = false;
    }
    // No text selection while a card is pressed or dragged. Not a preventDefault on the press: that
    // also stops the choose button from taking the focus (internal review F4.1).
    function onSelectStart(event: Event) {
      if (drag.current) event.preventDefault();
    }

    liftRef.current = lift;
    finishRef.current = finish;
    document.addEventListener("touchstart", onTouchStartAnywhere, true);
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd, { passive: false });
    document.addEventListener("touchcancel", onTouchCancel);
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("click", onClick, true);
    document.addEventListener("mousedown", onMouseDownAnywhere, true);
    document.addEventListener("selectstart", onSelectStart);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onLostFocus);
    return () => {
      document.removeEventListener("touchstart", onTouchStartAnywhere, true);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onLostFocus);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchCancel);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("mousedown", onMouseDownAnywhere, true);
      document.removeEventListener("selectstart", onSelectStart);
      const state = drag.current;
      if (state) {
        if (state.timer) clearTimeout(state.timer);
        cancelAnimationFrame(state.frame);
        state.ghost?.remove();
        drag.current = null;
      }
    };
  }, [board]);

  function begin(id: string, card: HTMLElement, x: number, y: number, touchId: number | null) {
    finishRef.current(false);
    const touch = touchId !== null;
    const rect = card.getBoundingClientRect();
    drag.current = {
      id,
      card,
      touch,
      touchId,
      x,
      y,
      startX: x,
      startY: y,
      offsetX: x - rect.left,
      offsetY: y - rect.top,
      lifted: false,
      ghost: null,
      target: null,
      timer: touch ? setTimeout(() => liftRef.current(), HOLD_MS) : null,
      frame: 0,
    };
    if (touch) setPressing(id);
  }

  /** The press on a card. The "Move to" menu on the card keeps its own touch and click. */
  function cardHandlers(id: string) {
    return {
      onTouchStart: (event: ReactTouchEvent<HTMLElement>) => {
        const touch = event.touches[0];
        if (event.touches.length > 1 || !touch || (event.target as HTMLElement).closest("select")) return;
        begin(id, event.currentTarget, touch.clientX, touch.clientY, touch.identifier);
      },
      onMouseDown: (event: ReactMouseEvent<HTMLElement>) => {
        if (event.button !== 0 || (event.target as HTMLElement).closest("select")) return;
        begin(id, event.currentTarget, event.clientX, event.clientY, null);
      },
      // A long press must lift the card, not open the phone's menu.
      onContextMenu: (event: ReactMouseEvent<HTMLElement>) => event.preventDefault(),
    };
  }

  return { pressing, lifted, over, cardHandlers };
}
